<template>
  <div>
    <v-row
      v-for="version in versions"
      :key="version.title"
    >
      <v-col>
        <v-card flat>
          <v-card-title>
            {{ version.title }}
          </v-card-title>

          <v-card-subtitle v-if="version.subtitle">
            {{ version.subtitle }}
          </v-card-subtitle>

          <v-card-text>
            <div
              v-if="version.summary"
              class="mb-4"
            >
              {{ version.summary }}
            </div>

            <div
              v-if="version.changes"
              class="text-subtitle-1"
            >
              Tilføjelser og ændringer
            </div>

            <ul class="mb-4">
              <li
                v-for="(change, i) in version.changes"
                :key="i"
              >
                {{ change }}.
              </li>
            </ul>

            <div
              v-if="version.fixes"
              class="text-subtitle-1"
            >
              Fejlrettelser
            </div>

            <ul>
              <li
                v-for="(fix, i) in version.fixes"
                :key="i"
              >
                {{ fix }}.
              </li>
            </ul>
          </v-card-text>
        </v-card>

        <v-divider class="mt-4" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: 'Changelog',
  data() {
    return {
      versions: [
        {
          title: 'v1.16.0',
          subtitle: 'July 2025',
          changes: [
            'Added external reference on projects',
            'Added translations for English. You can change language from the profile view',
            'Added integration to Dalux API',
            'External users are now able to request access and admins are able to process the request from within TPA Portal'
          ],
          fixes: [
            'Authentication error led to some users caught in an endless loop',
            'Improved error handling'
          ]
        },
        {
          title: 'v1.15.0',
          subtitle: 'April 2025',
          changes: [
            'Only show active task requests, deliveries and support tickets',
            'Changed case number concept'
          ],
          fixes: [
            'Load data when switching between active cases on a location',
            'Summary PDF could not be created on closed projects'
          ]
        },
        {
          title: 'v1.14.0',
          subtitle: 'August 2024',
          changes: [
            'Optimized handling of user access to specific locations',
            'Locations without cases are now also shown in the overview',
            'Added system log for administrators'
          ],
          fixes: [
            'Better mobile menu',
            'Improved performance on, among others, locations',
            'Various small fixes and improvements'
          ]
        },
        {
          title: 'v1.13.0',
          subtitle: 'May 2024',
          changes: [
            'New internal tools that make customer handling more efficient'
          ],
          fixes: [
            'Some new users could in special cases experience not seeing the terms of use and therefore not gaining access',
            'Too many emails to followers on new delivery',
            'Timestamps on PDF of messages'
          ]
        },
        {
          title: 'v1.12.0',
          subtitle: 'October 2023',
          changes: [
            'Logging of messages on deliveries',
            'Introduction of specifications (beta)',
            '"Show only favorites" is now turned off by default',
            'Optimization of supplier management',
            'Improved item synchronization'
          ]
        },
        {
          title: 'v1.11.0',
          subtitle: 'March 2023',
          changes: [
            'Support, task bank, deliveries, and locations have received a new layout and now load data continuously instead of all at once. This provides better performance and a better experience',
            'To reduce waiting time on the same pages, searching is no longer triggered automatically'
          ]
        },
        {
          title: 'v1.10.0',
          subtitle: 'January 2023',
          changes: [
            'Item numbers and other relevant things can now easily be copied by clicking on them',
            'Other internal improvements to tools'
          ]
        },
        {
          title: 'v1.9.0',
          subtitle: 'December 2022',
          changes: [
            'Items can be marked as favorites',
            'Item list can be filtered to show only favorites',
            'Show "New price" in the item list if the price has changed within a given period (currently 90 days)',
            'Improved internal tools for administration'
          ],
          fixes: [
            'Various small fixes and improvements'
          ]
        },
        {
          title: 'v1.8.0',
          subtitle: 'November 2022',
          changes: [
            'Improved internal tools for administration'
          ],
          fixes: [
            'Various small fixes and improvements'
          ]
        },
        {
          title: 'v1.7.0',
          subtitle: 'November 2022',
          changes: [
            'Added internal design module for deliveries',
            'Further improvement of material management'
          ]
        },
        {
          title: 'v1.6.0',
          subtitle: 'October 2022',
          changes: [
            'Improved product administration og overview'
          ]
        },
        {
          title: 'v1.5.1',
          subtitle: 'September 2022',
          changes: [
            'Activities on completed deliveries are no longer shown on the front page',
            'You can now distinguish between open and closed cases in lists',
            'Agreements can no longer be added to a completed delivery'
          ],
          fixes: [
            'SMS for new support case now correctly displays case details',
            'Other minor bug fixes'
          ]
        },
        {
          title: 'v1.5.0',
          subtitle: 'September 2022',
          changes: [
            'To the left of your name in the top right corner, you\'ll find a new filter that lets you see only the cases relevant to you',
            'Case cards show what else is happening at the location',
            'Messages and case progress have been added to deliveries',
            'Delivery agreements that have not yet been sent for approval are now more visibly marked as drafts',
            'You can now receive notifications when a support case with category B or C is created, as well as when a new message is posted on a delivery you follow',
            'Required date and expected number of technician days have been added to the Task Bank',
            'It is now possible to select more than one technical area on support cases',
            'It is now clearer where in the process a case is',
            'After creating a task in the Task Bank, you now receive a confirmation similar to deliveries and support',
            'When a delivery is created from the Task Bank, the same dialog is used as when creating a standalone delivery',
            'It is now possible to sort support cases by latest activity (e.g., a new file is uploaded or a new message)',
            'Only the relevant types, units, and technical areas for a case are shown to make the information easier to overview',
            'You now get a warning if you navigate away from an unsaved message on support and delivery cases',
            'We have removed the option to create a delivery from a closed task in the Task Bank',
            'Attention points are hidden from the list view if none exist on the case',
            'Management of users’ roles and responsibilities has been made more user-friendly',
            'The contact list has been made easier to navigate'
          ],
          fixes: [
            'In some cases, relative dates were calculated incorrectly for delivery activities due to different time zones',
            'Other minor bug fixes'
          ]
        },
        {
          title: 'v1.4.1',
          subtitle: 'December 2021',
          changes: [
            'When creating a delivery, selecting a coordinator and TPA delivery responsible is now required',
            'New menu item: "About the TPA Portal" where you can find previous versions and their changes',
            'Comments on approval are now also shown on the PDF of approved delivery agreements'
          ]
        },
        {
          title: 'v1.4.0',
          subtitle: 'December 2021',
          changes: [
            'Notifications via SMS and email now show information about location, agreement type, assigned roles, and the name of the phase a task you are following has changed to',
            '"Department" changed to "Unit"',
            'Added information about how to contact support under the FAQ',
            'Sort upcoming delivery activities by date on the front page'
          ],
          fixes: [
            'You were asked to log in too often',
            'Long text input in project description',
            'It\'s no longer possible to set expected end to be before expected start (or vice versa)',
            'Expected date in backlog changed to today when saving',
            'Link in some notification emails about projects were sent to the wrong recipient',
            'It was not possible to download reports on closed support tickets',
            'Other, smaller fixes'
          ]
        }
      ]
    }
  }
}
</script>